import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import customerService from '../../services/CustomerService';
import practitionerService from '../../services/PractitionerService';
import treatmentService from '../../services/TreatmentService';
import appointmentService from '../../services/AppointmentService';
import { Card, Button, Input, Select, CustomDropdown } from '../ui';

/**
 * TreatmentRecordForm component for creating and editing treatment records
 */
const TreatmentRecordForm = ({
  record,
  onSubmit,
  onCancel,
  isLoading,
  preSelectedCustomerId = null,
  preSelectedAppointmentId = null
}) => {
  // State for form data
  const [formData, setFormData] = useState({
    customerId: '',
    treatmentId: '',
    treatmentDate: '',
    practitionerId: '',
    appointmentId: '',
    diagnosis: '',
    summary: '',
    treatment: '',
    recommendations: '',
    followUp: false,
    followUpDate: '',
  });

  // Reference data
  const [customers, setCustomers] = useState([]);
  const [practitioners, setPractitioners] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [selectedCustomerAppointments, setSelectedCustomerAppointments] = useState([]);

  // Load reference data
  useEffect(() => {
    const fetchReferenceData = async () => {
      try {
        // Load customers
        const customersData = customerService.getAllCustomers();
        setCustomers(customersData);

        // Load practitioners
        const practitionersData = practitionerService.getAllPractitioners()
          .filter(p => p.isActive);
        setPractitioners(practitionersData);

        // Load treatments
        const treatmentsData = treatmentService.getAllTreatments()
          .filter(t => t.isActive);
        setTreatments(treatmentsData);

        // Load all appointments
        const appointmentsData = appointmentService.getAllAppointments();
        setAppointments(appointmentsData);

      } catch (err) {
        console.error('Error loading reference data:', err);
      }
    };

    fetchReferenceData();
  }, []);

  // Initialize form with record data or pre-selected customer/appointment
  useEffect(() => {
    if (record) {
      setFormData({
        customerId: record.customerId || '',
        treatmentId: record.treatmentId || '',
        treatmentDate: record.treatmentDate ? formatDateForInput(record.treatmentDate) : '',
        practitionerId: record.practitionerId || '',
        appointmentId: record.appointmentId || '',
        diagnosis: record.diagnosis || '',
        summary: record.summary || '',
        treatment: record.treatment || '',
        recommendations: record.recommendations || '',
        followUp: !!record.followUp,
        followUpDate: record.followUpDate ? formatDateForInput(record.followUpDate) : '',
      });
    } else {
      // For new records, set current date as default
      const today = new Date();
      const formattedDate = formatDateForInput(today.toISOString());

      setFormData(prev => ({
        ...prev,
        treatmentDate: formattedDate
      }));

      // If customer ID is pre-selected
      if (preSelectedCustomerId) {
        setFormData(prev => ({
          ...prev,
          customerId: preSelectedCustomerId
        }));
      }

      // If appointment ID is pre-selected
      if (preSelectedAppointmentId) {
        const selectedAppointment = appointments.find(a => a.id === preSelectedAppointmentId);

        if (selectedAppointment) {
          setFormData(prev => ({
            ...prev,
            appointmentId: preSelectedAppointmentId,
            customerId: selectedAppointment.customerId,
            treatmentId: selectedAppointment.treatmentId,
            practitionerId: selectedAppointment.practitionerId,
            treatmentDate: formatDateForInput(selectedAppointment.appointmentTime)
          }));
        }
      }
    }
  }, [record, preSelectedCustomerId, preSelectedAppointmentId, appointments]);

  // Update appointments when customer changes
  useEffect(() => {
    if (formData.customerId) {
      const customerAppointments = appointments.filter(a =>
        a.customerId === formData.customerId &&
        (a.status === 'scheduled' || a.status === 'confirmed' || a.status === 'completed')
      );
      setSelectedCustomerAppointments(customerAppointments);
    } else {
      setSelectedCustomerAppointments([]);
    }
  }, [formData.customerId, appointments]);

  // Format date for input
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  // Format datetime for display
  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);

    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const val = type === 'checkbox' ? checked : value;

    setFormData(prev => ({
      ...prev,
      [name]: val
    }));

    // If selecting an appointment, update related fields
    if (name === 'appointmentId' && value) {
      const selectedAppointment = appointments.find(a => a.id === value);
      if (selectedAppointment) {
        setFormData(prev => ({
          ...prev,
          treatmentId: selectedAppointment.treatmentId,
          practitionerId: selectedAppointment.practitionerId
        }));
      }
    }
  };

  // Handle customer selection change
  const handleCustomerChange = (e) => {
    const customerId = e.target.value;

    setFormData(prev => ({
      ...prev,
      customerId,
      appointmentId: '' // Reset appointment when customer changes
    }));
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    // Format dates for submission
    const processedData = {
      ...formData,
      treatmentDate: formData.treatmentDate ? new Date(formData.treatmentDate).toISOString() : null,
      followUpDate: formData.followUpDate ? new Date(formData.followUpDate).toISOString() : null,
    };

    onSubmit(processedData);
  };

  // Get customer name by ID
  const getCustomerName = (id) => {
    const customer = customers.find(c => c.id === id);
    return customer ? customer.name : '';
  };

  // Get treatment name by ID
  const getTreatmentName = (id) => {
    const treatment = treatments.find(t => t.id === id);
    return treatment ? treatment.name : '';
  };

  // Get practitioner name by ID
  const getPractitionerName = (id) => {
    const practitioner = practitioners.find(p => p.id === id);
    return practitioner ? practitioner.name : '';
  };

  // Grouped treatment options
  const groupedTreatmentOptions = [
    {
      label: '特色调理项目',
      options: treatments.filter(t => t.type === 'project').map(t => ({ value: t.id, label: t.name }))
    },
    {
      label: '基础项目',
      options: treatments.filter(t => t.type === 'basic').map(t => ({ value: t.id, label: t.name }))
    }
  ];

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Basic Information Section */}
      <Card>
        <div className="md:grid md:grid-cols-3 md:gap-6">
          <div className="md:col-span-1">
            <h3 className="text-lg font-medium leading-6 text-gray-900">基本信息</h3>
            <p className="mt-1 text-sm text-gray-500">
              调理记录的基本信息，包括客户、调理项目和日期
            </p>
          </div>
          <div className="mt-5 md:col-span-2 md:mt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Customer Selection */}
              <CustomDropdown
                label="客户"
                value={formData.customerId}
                onChange={handleCustomerChange}
                name="customerId"
                disabled={!!preSelectedCustomerId || !!preSelectedAppointmentId}
                required
                options={[
                  { value: '', label: '选择客户' },
                  ...customers.map(customer => ({
                    value: customer.id,
                    label: `${customer.name}${customer.phoneNumber ? ` (${customer.phoneNumber})` : ''}`
                  }))
                ]}
                placeholder="选择客户"
              />

              {/* Treatment Date */}
              <Input
                label="调理日期"
                type="date"
                name="treatmentDate"
                value={formData.treatmentDate}
                onChange={handleInputChange}
                required
              />

              {/* Appointment Selection (if customer is selected) */}
              {formData.customerId && (
                <div className="md:col-span-2">
                  <CustomDropdown
                    label="关联预约"
                    value={formData.appointmentId}
                    onChange={handleInputChange}
                    name="appointmentId"
                    disabled={!!preSelectedAppointmentId}
                    options={[
                      { value: '', label: '无关联预约' },
                      ...selectedCustomerAppointments.map(appointment => ({
                        value: appointment.id,
                        label: `${formatDateTime(appointment.appointmentTime)} - ${getTreatmentName(appointment.treatmentId)}`
                      }))
                    ]}
                    placeholder="选择关联预约"
                  />
                </div>
              )}

              {/* Treatment Selection */}
              <CustomDropdown
                label="调理项目"
                value={formData.treatmentId}
                onChange={handleInputChange}
                name="treatmentId"
                required
                options={[]}
                groupedOptions={groupedTreatmentOptions}
                placeholder="选择调理项目"
              />

              {/* Show treatment details when selected */}
              {(() => {
                const selectedTreatment = treatments.find(t => t.id === formData.treatmentId);
                if (!selectedTreatment) return null;
                return (
                  <div className="mt-4 p-4 bg-gray-50 rounded-xl border border-gray-200">
                    <div className="font-bold mb-2">{selectedTreatment.name}</div>
                    <div className="text-sm text-gray-700 mb-1">{selectedTreatment.description}</div>
                    {selectedTreatment.applicableConditions?.length > 0 && (
                      <div className="text-xs text-gray-500 mb-1">适应症：{selectedTreatment.applicableConditions.join('、')}</div>
                    )}
                    {selectedTreatment.contraindications?.length > 0 && (
                      <div className="text-xs text-gray-500 mb-1">禁忌症：{selectedTreatment.contraindications.join('、')}</div>
                    )}
                    {selectedTreatment.preparationInstructions && (
                      <div className="text-xs text-gray-500 mb-1">注意事项：{selectedTreatment.preparationInstructions}</div>
                    )}
                    {selectedTreatment.type === 'project' && selectedTreatment.composedOf?.length > 0 && (
                      <div className="text-xs text-gray-500">包含基础项目：{selectedTreatment.composedOf.map(id => getTreatmentName(id)).join('、')}</div>
                    )}
                  </div>
                );
              })()}

              {/* Practitioner Selection */}
              <CustomDropdown
                label="调理师"
                value={formData.practitionerId}
                onChange={handleInputChange}
                name="practitionerId"
                required
                options={[
                  { value: '', label: '选择调理师' },
                  ...practitioners.map(practitioner => ({
                    value: practitioner.id,
                    label: practitioner.name
                  }))
                ]}
                placeholder="选择调理师"
              />
            </div>
          </div>
        </div>
      </Card>

      {/* Treatment Details Section */}
      <Card>
        <div className="md:grid md:grid-cols-3 md:gap-6">
          <div className="md:col-span-1">
            <h3 className="text-lg font-medium leading-6 text-gray-900">调理详情</h3>
            <p className="mt-1 text-sm text-gray-500">
              记录调理的相关信息，包括辨证、调理方案和总结
            </p>
          </div>
          <div className="mt-5 md:col-span-2 md:mt-0 space-y-6">
            {/* Diagnosis */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                辨证诊断
              </label>
              <textarea
                name="diagnosis"
                rows={3}
                value={formData.diagnosis}
                onChange={handleInputChange}
                className="w-full px-4 py-3 text-sm border border-gray-300 bg-white text-gray-900 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200"
                placeholder="记录客户的辨证结果和诊断情况"
              />
            </div>

            {/* Treatment */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                调理方案
              </label>
              <textarea
                name="treatment"
                rows={4}
                value={formData.treatment}
                onChange={handleInputChange}
                className="w-full px-4 py-3 text-sm border border-gray-300 bg-white text-gray-900 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200"
                placeholder="记录具体的调理方法、手法和使用工具等"
              />
            </div>

            {/* Summary */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                调理总结 <span className="text-red-500">*</span>
              </label>
              <textarea
                name="summary"
                rows={3}
                value={formData.summary}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 text-sm border border-gray-300 bg-white text-gray-900 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200"
                placeholder="对本次调理效果的简要总结"
              />
            </div>
          </div>
        </div>
      </Card>

      {/* Follow-up Section */}
      <Card>
        <div className="md:grid md:grid-cols-3 md:gap-6">
          <div className="md:col-span-1">
            <h3 className="text-lg font-medium leading-6 text-gray-900">后续跟进</h3>
            <p className="mt-1 text-sm text-gray-500">
              记录需要跟进的建议和下次随访时间
            </p>
          </div>
          <div className="mt-5 md:col-span-2 md:mt-0 space-y-6">
            {/* Recommendations */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                健康建议
              </label>
              <textarea
                name="recommendations"
                rows={3}
                value={formData.recommendations}
                onChange={handleInputChange}
                className="w-full px-4 py-3 text-sm border border-gray-300 bg-white text-gray-900 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200"
                placeholder="给客户的健康建议和注意事项"
              />
            </div>

            {/* Follow-up Checkbox */}
            <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-xl border border-gray-200">
              <input
                id="followUp"
                name="followUp"
                type="checkbox"
                checked={formData.followUp}
                onChange={handleInputChange}
                className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500 focus:ring-offset-0"
              />
              <div>
                <label htmlFor="followUp" className="text-sm font-medium text-gray-900">
                  需要随访
                </label>
                <p className="text-xs text-gray-500 mt-1">
                  勾选此项以安排后续跟进
                </p>
              </div>
            </div>

            {/* Follow-up Date */}
            {formData.followUp && (
              <Input
                label="随访日期"
                type="date"
                name="followUpDate"
                value={formData.followUpDate}
                onChange={handleInputChange}
              />
            )}
          </div>
        </div>
      </Card>

      {/* Form Actions */}
      <div className="flex justify-end space-x-3 pt-6 border-t border-gray-100">
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          disabled={isLoading}
        >
          取消
        </Button>
        <Button
          type="submit"
          disabled={isLoading}
          loading={isLoading}
        >
          {record ? '保存修改' : '创建记录'}
        </Button>
      </div>
    </form>
  );
};

TreatmentRecordForm.propTypes = {
  record: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  preSelectedCustomerId: PropTypes.string,
  preSelectedAppointmentId: PropTypes.string
};

TreatmentRecordForm.defaultProps = {
  isLoading: false
};

export default TreatmentRecordForm;